import { apiClient } from './api';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string;
  isActive: boolean;
  emailVerified: boolean;
  mfaEnabled: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  roles: Role[];
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  isSystemRole?: boolean;
}

export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleIds?: string[];
  isActive?: boolean;
}

export interface UpdateUserData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  isActive?: boolean;
  emailVerified?: boolean;
}

export interface UsersListParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  roleId?: string;
}

export interface UsersListResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class UsersService {
  /**
   * Get paginated list of users
   */
  async getUsers(params?: UsersListParams): Promise<UsersListResponse> {
    const response = await apiClient.get<UsersListResponse>('/api/v1/users', {
      params,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch users');
    }

    return response.data;
  }

  /**
   * Search users by query
   */
  async searchUsers(query: string, limit = 25): Promise<User[]> {
    const response = await apiClient.get<UsersListResponse>('/api/v1/users', {
      params: { search: query, limit },
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to search users');
    }

    return response.data.users;
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get<{ user: User }>(`/api/v1/users/${id}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch user');
    }

    return response.data.user;
  }

  /**
   * Create new user
   */
  async createUser(data: CreateUserData): Promise<User> {
    const response = await apiClient.post<{ user: User }>('/api/v1/users', data);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to create user');
    }

    return response.data.user;
  }

  /**
   * Update user
   */
  async updateUser(id: string, data: UpdateUserData): Promise<User> {
    const response = await apiClient.put<{ user: User }>(`/api/v1/users/${id}`, data);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to update user');
    }

    return response.data.user;
  }

  /**
   * Soft delete user
   */
  async deleteUser(id: string): Promise<void> {
    const response = await apiClient.delete(`/api/v1/users/${id}`);

    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete user');
    }
  }

  /**
   * Assign role to user
   */
  async assignRole(userId: string, roleId: string): Promise<User> {
    const response = await apiClient.post<{ user: User }>(
      `/api/v1/users/${userId}/roles`,
      { roleId }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to assign role');
    }

    return response.data.user;
  }

  /**
   * Remove role from user
   */
  async removeRole(userId: string, roleId: string): Promise<User> {
    const response = await apiClient.delete<{ user: User }>(
      `/api/v1/users/${userId}/roles/${roleId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to remove role');
    }

    return response.data.user;
  }

  /**
   * Get all available roles
   */
  async getRoles(): Promise<Role[]> {
    const response = await apiClient.get<{ roles: Role[] }>('/api/v1/users/roles');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to fetch roles');
    }

    return response.data.roles;
  }

  /**
   * Helper: Get role display name
   */
  getRoleDisplayName(role: Role): string {
    return role.name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Helper: Get user status badge color
   */
  getUserStatusColor(user: User): 'success' | 'danger' | 'warning' {
    if (!user.isActive) {return 'danger';}
    if (!user.emailVerified) {return 'warning';}
    return 'success';
  }

  /**
   * Helper: Get user status text
   */
  getUserStatusText(user: User): string {
    if (!user.isActive) {return 'Inactive';}
    if (!user.emailVerified) {return 'Unverified';}
    return 'Active';
  }
}

export const usersService = new UsersService();
